import { inject, Injectable } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import { ICbdItem } from '../interfaces/ICbdItem';
import { localizeWithFallback } from '@zendra/shared';

@Injectable({
  providedIn: 'root',
})
export class MenuTransformService {
  private localizationService = inject(LocalizationService);

  translateItems(items: ICbdItem[]): ICbdItem[] {
    return items.map(item => this.translateItem(item));
  }

  private translateItem(item: ICbdItem): ICbdItem {
    return {
      ...item,
      title: localizeWithFallback(this.localizationService, item.title || ''),
      subcategories: item.subcategories?.map(subcategory => ({
        ...subcategory,
        ...(subcategory.title && {
          title: localizeWithFallback(this.localizationService, subcategory.title),
        }),
        links: subcategory.links?.map(link => ({
          ...link,
          title: localizeWithFallback(this.localizationService, link.title || ''),
        })),
      })),
    };
  }

  sortAlphabetically(items: ICbdItem[]): ICbdItem[] {
    const currentLocale = this.localizationService.currentLang;
    const getSubcategorySortKey = (subcategory: any) =>
      subcategory.title || subcategory.links?.[0]?.title || '';

    return items
      .map(category => ({
        ...category,
        subcategories: category.subcategories
          ?.map(subcategory => ({
            ...subcategory,
            links: subcategory.links?.sort((a, b) =>
              a.title.localeCompare(b.title, currentLocale)
            ),
          }))
          .sort((a, b) => {
            const keyA = getSubcategorySortKey(a);
            const keyB = getSubcategorySortKey(b);
            return keyA.localeCompare(keyB, currentLocale);
          }),
      }))
      .sort((a, b) => a.title.localeCompare(b.title, currentLocale));
  }

  sortByTranslatedTitles(items: ICbdItem[]): ICbdItem[] {
    const currentLocale = this.localizationService.currentLang;
    const getSubcategorySortKey = (subcategory: any) =>
      subcategory.title || subcategory.links?.[0]?.title || '';

    return items
      .map(category => ({
        ...category,
        subcategories: category.subcategories
          ?.map(subcategory => ({
            ...subcategory,
            links: subcategory.links?.sort((a, b) => {
              const titleA = localizeWithFallback(this.localizationService, a.title || '');
              const titleB = localizeWithFallback(this.localizationService, b.title || '');
              return titleA.localeCompare(titleB, currentLocale);
            }),
          }))
          .sort((a, b) => {
            const keyA = localizeWithFallback(this.localizationService, getSubcategorySortKey(a));
            const keyB = localizeWithFallback(this.localizationService, getSubcategorySortKey(b));
            return keyA.localeCompare(keyB, currentLocale);
          }),
      }))
      .sort((a, b) => {
        const titleA = localizeWithFallback(this.localizationService, a.title);
        const titleB = localizeWithFallback(this.localizationService, b.title);
        return titleA.localeCompare(titleB, currentLocale);
      });
  }
}
